import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { RefreshCw, Code, Trash2, Users, Plus, Palette } from "lucide-react";

export function FormEditorDialog({
  draft, setDraft, editingId, saving, save, team, previewRef, postPreview,
  togglePool, updateRule, addRule, removeRule, onClose,
}) {
  return (
    <Dialog open={!!draft} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="form-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editingId ? "Edit Form" : "New Form"}</DialogTitle>
        </DialogHeader>
        {draft && (
          <div className="grid lg:grid-cols-[1fr_340px] gap-5 items-start">
            <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Form Name (internal)</Label>
                <Input data-testid="form-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1" placeholder="Homepage Form" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Your Company Name</Label>
                <Input data-testid="form-company" value={draft.company_name} onChange={(e) => setDraft({ ...draft, company_name: e.target.value })} className="mt-1" placeholder="Great Marketing LLC" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Form Title (public)</Label>
                <Input data-testid="form-title" value={draft.form_title} onChange={(e) => setDraft({ ...draft, form_title: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Booking URL</Label>
                <Input data-testid="form-booking" value={draft.booking_url} onChange={(e) => setDraft({ ...draft, booking_url: e.target.value })} className="mt-1" placeholder="https://calendly.com/..." />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Description</Label>
              <Textarea data-testid="form-desc" value={draft.form_description} onChange={(e) => setDraft({ ...draft, form_description: e.target.value })} className="mt-1 h-14 text-sm" />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold">Brand Color</Label>
                <input type="color" value={draft.primary_color} onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })} className="w-9 h-8 rounded border cursor-pointer" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch data-testid="form-active" checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
                <span className="text-xs font-semibold">Active</span>
              </label>
            </div>

            {/* Design / Appearance */}
            <div className="pt-3 border-t border-slate-100">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Palette className="w-3 h-3" /> Appearance</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs font-semibold">Layout</Label>
                  <Select value={draft.layout} onValueChange={(v) => setDraft({ ...draft, layout: v })}>
                    <SelectTrigger data-testid="form-layout" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="centered">Centered card</SelectItem>
                      <SelectItem value="split">Split (brand panel + form)</SelectItem>
                      <SelectItem value="compact">Compact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Theme</Label>
                  <Select value={draft.theme} onValueChange={(v) => setDraft({ ...draft, theme: v })}>
                    <SelectTrigger data-testid="form-theme" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs font-semibold">Logo URL (optional)</Label>
                <Input data-testid="form-logo-url" value={draft.logo_url} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} className="mt-1" placeholder="https://yoursite.com/logo.png" />
                <p className="text-[10px] text-muted-foreground mt-1">Shown at the top of the form. Leave blank to use a colored icon.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs font-semibold">Background</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      data-testid="form-bg-color"
                      value={draft.background_color && draft.background_color !== "transparent" ? draft.background_color : "#f8fafc"}
                      disabled={draft.background_color === "transparent"}
                      onChange={(e) => setDraft({ ...draft, background_color: e.target.value })}
                      className="w-9 h-8 rounded border cursor-pointer disabled:opacity-40"
                    />
                    <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                      <Checkbox
                        data-testid="form-bg-transparent"
                        checked={draft.background_color === "transparent"}
                        onCheckedChange={(v) => setDraft({ ...draft, background_color: v ? "transparent" : "" })}
                      />
                      Transparent (blend with my site)
                    </label>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Form Width</Label>
                  <Select value={String(draft.form_width)} onValueChange={(v) => setDraft({ ...draft, form_width: parseInt(v, 10) })}>
                    <SelectTrigger data-testid="form-width" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="400">Narrow (400px)</SelectItem>
                      <SelectItem value="520">Standard (520px)</SelectItem>
                      <SelectItem value="640">Wide (640px)</SelectItem>
                      <SelectItem value="800">Extra wide (800px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs font-semibold">Card Style</Label>
                <Select value={draft.card_style} onValueChange={(v) => setDraft({ ...draft, card_style: v })}>
                  <SelectTrigger data-testid="form-card-style" className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card">Card (background + shadow)</SelectItem>
                    <SelectItem value="flat">Flat (thin border, no shadow)</SelectItem>
                    <SelectItem value="transparent">Transparent (no card — fields on page)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                For seamless embedding, choose <strong>Transparent</strong> — the embed code auto-resizes to fit your page with no scrollbars.
              </p>
            </div>

            {/* Routing */}
            <div className="pt-3 border-t border-slate-100">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Lead Routing</Label>
              <Select value={draft.routing_mode} onValueChange={(v) => setDraft({ ...draft, routing_mode: v })}>
                <SelectTrigger data-testid="form-routing-mode" className="mt-1.5 h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="specific">Specific rep — all leads go to one person</SelectItem>
                  <SelectItem value="round_robin">Round-robin — rotate evenly across a pool</SelectItem>
                  <SelectItem value="geography">By geography — route by lead's state</SelectItem>
                  <SelectItem value="manual">Manual — leave unassigned for a manager</SelectItem>
                </SelectContent>
              </Select>

              {draft.routing_mode === "specific" && (
                <div className="mt-3">
                  <Label className="text-xs font-semibold">Assign all leads to</Label>
                  <Select value={draft.assigned_user_id || ""} onValueChange={(v) => setDraft({ ...draft, assigned_user_id: v })}>
                    <SelectTrigger data-testid="form-specific-user" className="mt-1 h-9 text-sm"><SelectValue placeholder="Select a rep" /></SelectTrigger>
                    <SelectContent>{team.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name || t.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              {draft.routing_mode === "round_robin" && (
                <div className="mt-3">
                  <Label className="text-xs font-semibold flex items-center gap-1"><Users className="w-3 h-3" /> Rotation pool</Label>
                  <div className="mt-1.5 space-y-1.5 max-h-44 overflow-y-auto p-2 rounded border border-slate-200">
                    {team.map((t) => (
                      <label key={t.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={draft.pool_user_ids.includes(t.user_id)} onCheckedChange={() => togglePool(t.user_id)} data-testid={`pool-${t.user_id}`} />
                        {t.name || t.email}
                      </label>
                    ))}
                    {team.length === 0 && <p className="text-xs text-muted-foreground">No team members yet. Add reps via Google login, then configure the pool.</p>}
                  </div>
                </div>
              )}

              {draft.routing_mode === "geography" && (
                <div className="mt-3 space-y-2">
                  {draft.geo_rules.map((rule, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`geo-rule-${i}`}>
                      <Input
                        value={(rule.states || []).join(", ")}
                        onChange={(e) => updateRule(i, { states: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        placeholder="TX, OK, LA"
                        className="h-9 text-sm flex-1"
                      />
                      <Select value={rule.user_id || ""} onValueChange={(v) => updateRule(i, { user_id: v })}>
                        <SelectTrigger className="h-9 text-sm w-44"><SelectValue placeholder="Rep" /></SelectTrigger>
                        <SelectContent>{team.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name || t.email}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" className="h-9 text-rose-600" onClick={() => removeRule(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  <Button data-testid="add-geo-rule" variant="outline" size="sm" className="h-8 text-xs" onClick={addRule}><Plus className="w-3 h-3 mr-1" /> Add rule</Button>
                  <p className="text-[11px] text-muted-foreground p-2 rounded bg-slate-50 border border-slate-200">
                    Leads are matched to a rep by these rules, then by each rep's <strong>Territory</strong> (set on the Team tab). If no territory matches the lead's state, they're distributed <strong>round-robin</strong> across reps who have a territory — or use the final fallback below.
                  </p>
                  <div className="pt-1">
                    <Label className="text-xs font-semibold">Final fallback rep (if no reps have territories)</Label>
                    <Select value={draft.geo_fallback_user_id || ""} onValueChange={(v) => setDraft({ ...draft, geo_fallback_user_id: v })}>
                      <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Select a fallback rep" /></SelectTrigger>
                      <SelectContent>{team.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name || t.email}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {draft.routing_mode === "manual" && (
                <p className="mt-2 text-xs text-muted-foreground p-2 rounded bg-slate-50 border border-slate-200">
                  Leads will arrive <strong>Unassigned</strong>. A manager can assign each one to a rep from the lead's detail page.
                </p>
              )}
            </div>

            </div>
            {/* Live preview */}
            <div className="hidden lg:block">
              <div className="sticky top-0 space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Live Preview</Label>
                <div className="rounded-xl border border-slate-200 bg-slate-100 overflow-hidden" style={{ height: 540 }}>
                  <iframe
                    ref={previewRef}
                    title="Form preview"
                    data-testid="form-live-preview"
                    src={`${window.location.origin}/audit-form?form_id=preview&preview=1`}
                    onLoad={postPreview}
                    className="w-full h-full border-0"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">Updates live as you edit. Transparent backgrounds show the host page color when embedded.</p>
              </div>
            </div>
          </div>
        )}

        {draft && (
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="save-form-btn" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Code className="w-4 h-4 mr-1" />} {editingId ? "Update Form" : "Create Form"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
